import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Nullable } from 'corgi-types';

interface CreateNewObserverArgs {
  observerRef: MutableRefObject<Nullable<IntersectionObserver>>;
  options: CreateNewObserverOptions;
  setIsIntersecting: Dispatch<SetStateAction<boolean>>;
  disconnectOnIntersection?: boolean;
}

export interface CreateNewObserverOptions extends IntersectionObserverInit {
  disconnectOnIntersection?: boolean;
}

interface ObserveNodeArgs extends CreateNewObserverArgs {
  node: Nullable<Element>;
}

const defaultObserverOptions = {
  root: null,
  rootMargin: '0px',
  threshold: [0],
  disconnectOnIntersection: true,
};

const createNewObserver = ({
  observerRef,
  setIsIntersecting,
  options: { disconnectOnIntersection = true, ...options },
}: CreateNewObserverArgs) =>
  new IntersectionObserver(([{ isIntersecting }]) => {
    setIsIntersecting(isIntersecting);

    if (isIntersecting && disconnectOnIntersection) {
      observerRef.current.disconnect();
    }
  }, options);

const observeNode = ({ node, observerRef, setIsIntersecting, options }: ObserveNodeArgs) => {
  if (!observerRef.current) {
    observerRef.current = createNewObserver({
      observerRef,
      options,
      setIsIntersecting,
    });
  }

  if (node) {
    observerRef.current.observe(node);
  } else {
    observerRef.current.disconnect();
  }
};

export const useIsObserved = (options: CreateNewObserverOptions = defaultObserverOptions) => {
  const observerRef = useRef<IntersectionObserver>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);

  const setNodeRef = useCallback(
    (node: Nullable<Element>) => {
      observeNode({ node, observerRef, options, setIsIntersecting });
    },
    [options]
  );

  return [setNodeRef, isIntersecting] as const;
};

export const useIsTargetObserved = (
  targetRef: MutableRefObject<Nullable<HTMLElement>>,
  options: CreateNewObserverOptions = defaultObserverOptions
) => {
  const observerRef = useRef<IntersectionObserver>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    observeNode({
      node: targetRef.current,
      observerRef,
      options,
      setIsIntersecting,
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return isIntersecting;
};
